import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AssetCard,
  EmptyState,
  StatusPill,
  type StatusLevel,
} from '@/lib';
import { FleetTable } from './FleetTable';
import { FleetMap } from './FleetMap';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import { goAnalyze, goAsset } from '@/app/navigation';
import { orgRoute } from '@/app/org-route';
import type { HealthPeriod, HealthStatus } from '@/lib';
import { Route as FleetRoute } from '@/routes/$orgId.fleet';
import { photoForAsset } from '@/screens/S5SignalAnalysis/fixtures';

type FleetAsset = {
  assetId: string;
  name: string;
  assetClass: string;
  siteName: string;
  criticality: 'low' | 'medium' | 'high';
  currentHealthScore: number;
  currentHealthPrev?: number;
  openAlertCount: number;
  currentStatus: StatusLevel;
  rul_days?: number;
};

function useFleet(severity?: StatusLevel | 'all') {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['fleet', severity ?? 'all'],
    queryFn: async (): Promise<FleetAsset[]> => {
      const q = severity && severity !== 'all' ? `?severity=${severity}` : '';
      const res = await apiFetch(config, `/v1/assets${q}`);
      const body = (await res.json()) as { items: FleetAsset[] };
      return body.items;
    },
    staleTime: 30_000,
  });
}

function syntheticPeriods(seed: string): HealthPeriod[] {
  // Deterministic 30-day series per asset so cards have a stable
  // HealthTimeline strip until Phase 3 (2..N) bundles them in the list
  // response.
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) >>> 0;
  const states: HealthStatus[] = ['healthy', 'healthy', 'watch', 'warn', 'critical'];
  const out: HealthPeriod[] = [];
  for (let i = 29; i >= 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const pct = s / 233280;
    const recencyBoost = i < 8 ? 0.3 : 0;
    const idx = Math.min(
      states.length - 1,
      Math.floor((pct - recencyBoost + 0.4) * states.length),
    );
    out.push({
      at: new Date(Date.now() - i * 86_400_000),
      status: states[Math.max(0, idx)] ?? 'healthy',
    });
  }
  return out;
}

const SEVERITY_OPTIONS: { value: StatusLevel | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'warn', label: 'Warn' },
  { value: 'watch', label: 'Watch' },
];

/**
 * S2 Fleet (Cards default). Three-column AssetCard grid filtered by
 * severity. The Table/Map view-mode toggle ships visible but the
 * non-cards views are stubbed for follow-up PRs.
 */
export function S2Fleet() {
  const navigate = useNavigate();
  const search = FleetRoute.useSearch();
  const [severity, setSeverity] = useState<StatusLevel | 'all'>('all');
  const fleet = useFleet(severity);

  const grid = useMemo(() => {
    return (fleet.data ?? []).map((a) => ({
      ...a,
      periods: syntheticPeriods(a.assetId),
    }));
  }, [fleet.data]);

  if (fleet.isLoading) return <FleetSkeleton />;
  if (fleet.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load fleet"
        body="Retry, or check the asset-service status."
        errorCode={String(fleet.error)}
        cta={{ label: 'Retry', onClick: () => fleet.refetch() }}
      />
    );
  }

  const setView = (view: 'cards' | 'table' | 'map') => {
    navigate({
      to: orgRoute('/fleet'),
      search: { view, q: search.q },
      replace: true,
    });
  };

  return (
    <div className="flex flex-col gap-sp-4">
      <header className="flex flex-wrap items-baseline justify-between gap-sp-3">
        <h1 className="text-fs-h1 font-medium text-gray-900">Fleet</h1>
        <div className="flex items-center gap-sp-3">
          <p className="text-fs-meta text-gray-700">
            {grid.length} asset{grid.length === 1 ? '' : 's'}
          </p>
          <a
            href={orgRoute('/assets/new')}
            className="inline-flex items-center rounded-r-default bg-info px-sp-3 py-sp-1 text-fs-meta font-medium text-gray-50 hover:opacity-90"
          >
            + Add asset
          </a>
          <a
            href={orgRoute('/sites/new')}
            className="inline-flex items-center rounded-r-default border border-gray-300 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:bg-gray-100"
          >
            + Add site
          </a>
          {/* View segmented control. Persists to the URL so deep-links
              + browser-back keep the operator's chosen view. */}
          <div
            role="tablist"
            aria-label="Fleet view"
            className="inline-flex overflow-hidden rounded-r-default border border-gray-300 bg-gray-50"
          >
            {(
              [
                { key: 'cards', label: 'Cards' },
                { key: 'table', label: 'Table' },
                { key: 'map', label: 'Map' },
              ] as const
            ).map((v) => {
              const active = search.view === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setView(v.key)}
                  className={
                    'px-sp-3 py-sp-1 text-fs-meta font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
                    (active
                      ? 'bg-gray-900 text-gray-50'
                      : 'text-gray-700 hover:bg-gray-100')
                  }
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-sp-2">
        <span className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Severity
        </span>
        {SEVERITY_OPTIONS.map((opt) => {
          const active = severity === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => setSeverity(opt.value)}
              className={
                'rounded-r-chip px-sp-2 py-sp-1 text-fs-meta font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
                (active
                  ? 'bg-gray-900 text-gray-50'
                  : 'border border-gray-300 bg-gray-50 text-gray-700 hover:border-gray-500')
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {search.view === 'cards' ? (
        grid.length === 0 ? (
          <EmptyState
            variant="filtered"
            title="No assets match these filters"
            body="Try widening severity or clearing the search query."
            cta={{ label: 'Clear filters', onClick: () => setSeverity('all') }}
          />
        ) : (
          <ul className="grid gap-sp-4 lg:grid-cols-3 md:grid-cols-2">
            {grid.map((a) => (
              <li key={a.assetId}>
                <AssetCard
                  asset={{
                    id: a.assetId,
                    name: a.name,
                    klass: a.assetClass,
                    site: a.siteName,
                    health: a.currentHealthScore,
                    previousHealth: a.currentHealthPrev,
                    thresholds: { warn: 40, critical: 20 },
                    severity: { level: a.currentStatus, label: a.currentStatus },
                    rul: a.rul_days ? { days: a.rul_days } : undefined,
                    openAlerts: a.openAlertCount,
                    // Photo URL from the S5 catalog. The catalog is
                    // the single source of truth for asset photos
                    // across screens; if the JPG is missing the
                    // AssetCardPhoto helper falls back to "IMG".
                    photo: photoForAsset(a.assetId),
                  }}
                  periods={a.periods}
                  onClick={() => goAsset(navigate, a.assetId)}
                  onAnalyze={() => goAnalyze(navigate, a.assetId)}
                />
              </li>
            ))}
          </ul>
        )
      ) : search.view === 'table' ? (
        grid.length === 0 ? (
          <EmptyState
            variant="filtered"
            title="No assets match these filters"
            body="Try widening severity or clearing the search query."
            cta={{ label: 'Clear filters', onClick: () => setSeverity('all') }}
          />
        ) : (
          <FleetTable rows={grid} />
        )
      ) : search.view === 'map' ? (
        <FleetMap assets={grid} />
      ) : (
        <FleetViewPlaceholder view={search.view} count={grid.length} />
      )}
    </div>
  );
}

function FleetSkeleton() {
  return (
    <div className="space-y-sp-4">
      <div className="h-9 w-24 animate-pulse rounded-r-chip bg-gray-100" />
      <ul className="grid gap-sp-4 lg:grid-cols-3 md:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <li key={i}>
            <div className="h-72 animate-pulse rounded-r-card bg-gray-100" />
          </li>
        ))}
      </ul>
    </div>
  );
}

function FleetViewPlaceholder({ view, count }: { view: string; count: number }) {
  return (
    <div className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-9 text-center">
      <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
        Fleet · {view}
      </p>
      <p className="mt-sp-2 text-fs-statement text-gray-900">
        {view === 'table' ? 'Table view' : 'Map view'} coming in Phase 4 (3/n)
      </p>
      <p className="mt-sp-2 text-fs-meta text-gray-700">
        {count} asset{count === 1 ? '' : 's'} would render here.
      </p>
      <div className="mt-sp-4 flex items-center justify-center gap-sp-2">
        <StatusPill level="info" label="Stub" variant="outlined" />
      </div>
    </div>
  );
}
